/**
 * SalonID nieuwsbrief-aanmelding (okt 2026, Baymard).
 *
 * Geen blokkerende popup meer. Een knop rechtsonder ("Salontips per mail") opent het
 * formulier: op desktop als kaart in de hoek, op mobiel als onderblad. Niet-modaal: geen
 * donkere laag, geen scroll-blokkade en geen focusval.
 *
 * Desktop: de kaart opent vanzelf na de ingestelde vertraging (data-delay) en blijft daarna
 * op volgende pagina's open tot hij wordt weggeklikt. Wegklikken (kruisje, Esc) maakt hem
 * klein: dan blijft alleen de knop, zo lang als de instelling 'frequency' zegt. Niet vanzelf
 * open zolang de taalsuggestie, een lade of het aanmeldblok in beeld is, iemand aan het typen
 * is of er iets in de winkelwagen zit. Mobiel: nooit vanzelf, alleen via de knop.
 *
 * De knop blijft weg:
 * - na een aanmelding via de popup of de footer (localStorage 'sid-nl-subscribed'), en voor
 *   wie via een nieuwsbriefmail binnenkomt (_hsenc of utm_medium=email in de URL);
 * - na "Nee, bedankt", zo lang als de instelling 'frequency' zegt ('once' = deze sessie);
 * - zolang de taalsuggestie in beeld is (die gaat voor);
 * - op de winkelwagenpagina (al in Liquid, hier nog eens voor de zekerheid).
 */
if (!customElements.get('newsletter-modal')) {
  const SUBSCRIBED_KEY = 'sid-nl-subscribed';
  const DECLINED_KEY = 'sid-nl-declined';
  const COLLAPSED_KEY = 'sid-nl-collapsed';
  const AUTOSHOWN_KEY = 'sid-nl-autoshown'; // sessionStorage: deze sessie al vanzelf geopend
  const DESKTOP = '(min-width: 768px)';
  // vaste balken onderaan waar de knop boven moet blijven (koopbalk, samplebox)
  const BOTTOM_BARS = '.product-buy-bar, .sid-sb__bar';
  // pas na de eerste indruk tonen, en na de taalsuggestie (die komt na 1,2 s)
  const TEASER_DELAY = 2000;

  const read = (storage, key) => {
    try { return storage.getItem(key); } catch (e) { return null; }
  };
  const write = (storage, key, value) => {
    try { storage.setItem(key, value); } catch (e) {}
  };

  customElements.define('newsletter-modal', class NewsletterModal extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.teaser = this.querySelector('[data-nl-teaser]');
      this.panel = this.querySelector('.sid-nl-panel');
      if (!this.teaser || !this.panel) return;
      this.isOpen = false;
      this.ready = false;

      this.markSubscriberFromUrl();

      this.teaser.addEventListener('click', () => this.open());
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-nl-close]')) {
          event.preventDefault();
          this.close();
        } else if (event.target.closest('[data-nl-decline]')) {
          event.preventDefault();
          this.decline();
        }
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.isOpen) this.close();
      });
      document.addEventListener('sid:newsletter-subscribed', () => {
        // na het event zet de kaart zelf (bij een aanmelding in de kaart) eerst zijn succesmelding neer
        setTimeout(() => {
          // aangemeld via het blok of de footer: kaart dicht; via de kaart zelf: succesmelding laten staan
          if (this.isOpen && !this.panel.classList.contains('posted_successfully')) this.close({ remember: false });
          else this.updateTeaser();
        }, 0);
      });

      const langSuggest = document.querySelector('.sid-langsug');
      if (langSuggest) {
        new MutationObserver(() => this.updateTeaser()).observe(langSuggest, { attributes: true, attributeFilter: ['hidden'] });
      }

      let liftQueued = false;
      const queueLift = () => {
        if (liftQueued) return;
        liftQueued = true;
        requestAnimationFrame(() => {
          liftQueued = false;
          this.liftAboveBottomBars();
        });
      };
      window.addEventListener('scroll', queueLift, { passive: true });
      window.addEventListener('resize', queueLift);
      // de koopbalk schuift met een overgang van 0,3 s in beeld
      document.addEventListener('transitionend', (event) => {
        if (event.target.matches && event.target.matches(BOTTOM_BARS)) queueLift();
      });

      // theme-editor: kaart tonen zolang de sectie geselecteerd is
      document.addEventListener('shopify:section:select', (event) => {
        if (event.detail.sectionId === this.dataset.sectionId) this.open({ focus: false });
      });
      document.addEventListener('shopify:section:deselect', (event) => {
        if (event.detail.sectionId === this.dataset.sectionId) this.close({ remember: false });
      });

      if (this.dataset.testMode === 'true') {
        this.ready = true;
        this.open({ focus: false });
        return;
      }

      setTimeout(() => {
        this.ready = true;
        this.updateTeaser();
      }, TEASER_DELAY);

      if (this.mayAutoOpen()) {
        // al eerder deze sessie vanzelf geopend en niet weggeklikt: op deze pagina meteen weer open
        const again = read(sessionStorage, AUTOSHOWN_KEY) === '1';
        const delay = again ? 0 : parseInt(this.dataset.delay, 10) || 0;
        setTimeout(() => this.autoOpen({ instant: again }), delay);
      }
    }

    markSubscriberFromUrl() {
      const params = new URLSearchParams(window.location.search);
      if (params.has('_hsenc') || params.get('utm_medium') === 'email') {
        write(localStorage, SUBSCRIBED_KEY, '1');
      }
    }

    isSubscribed() {
      return read(localStorage, SUBSCRIBED_KEY) === '1';
    }

    // 'once' = deze sessie; anders zo veel dagen als de instelling 'frequency' zegt
    remember(key) {
      if ((this.dataset.frequency || '') === 'once') write(sessionStorage, key, '1');
      else write(localStorage, key, String(Date.now()));
    }

    remembered(key) {
      const frequency = this.dataset.frequency || '7_days';
      if (frequency === 'once') return Boolean(read(sessionStorage, key));
      const then = parseInt(read(localStorage, key), 10);
      if (!then) return false;
      const days = parseInt(frequency.split('_')[0], 10) || 7;
      return Date.now() - then < days * 24 * 3600 * 1000;
    }

    isDeclined() {
      return this.remembered(DECLINED_KEY);
    }

    cartHasItems() {
      const cartBlip = document.querySelector('.cart-blip');
      return Boolean(cartBlip && !cartBlip.hidden && parseInt(cartBlip.textContent, 10) > 0);
    }

    mayAutoOpen() {
      return window.matchMedia(DESKTOP).matches
        && !this.isSubscribed()
        && !this.isDeclined()
        && !this.remembered(COLLAPSED_KEY)
        && !this.cartHasItems()
        && !document.querySelector('.sid-cartpage');
    }

    inlineBlockInView() {
      return Array.from(document.querySelectorAll('[data-sid-nl-inline]')).some((block) => {
        if (block.hidden) return false;
        const r = block.getBoundingClientRect();
        return r.height > 0 && r.top < window.innerHeight && r.bottom > 0;
      });
    }

    autoOpen({ instant = false } = {}) {
      if (this.isOpen || !this.mayAutoOpen()) return;
      const active = document.activeElement;
      const typing = active && active.matches('input, textarea, select') && !this.contains(active);
      const busy = this.langSuggestVisible()
        || document.body.classList.contains('overflow-hidden') // lade of modal open
        || typing
        || this.inlineBlockInView();
      if (busy) {
        setTimeout(() => this.autoOpen(), 3000);
        return;
      }
      write(sessionStorage, AUTOSHOWN_KEY, '1');
      if (instant) {
        this.classList.add('is-instant');
        setTimeout(() => this.classList.remove('is-instant'), 400);
      }
      this.open({ focus: false });
    }

    langSuggestVisible() {
      const langSuggest = document.querySelector('.sid-langsug');
      return Boolean(langSuggest && !langSuggest.hidden);
    }

    updateTeaser() {
      const show = this.ready
        && !this.isSubscribed()
        && !this.isDeclined()
        && !this.langSuggestVisible()
        && !document.querySelector('.sid-cartpage');
      this.teaser.hidden = !show;
      if (show) this.liftAboveBottomBars();
    }

    liftAboveBottomBars() {
      const vh = window.innerHeight;
      const vw = window.innerWidth;
      let lift = 0;
      document.querySelectorAll(BOTTOM_BARS).forEach((bar) => {
        const cs = window.getComputedStyle(bar);
        if (cs.position !== 'fixed' || cs.visibility === 'hidden' || cs.display === 'none' || parseFloat(cs.opacity) === 0) return;
        const r = bar.getBoundingClientRect();
        // alleen balken die tegen de onderrand liggen en de rechterhoek raken
        if (r.height > 0 && r.top < vh && vh - r.bottom < 32 && r.right > vw - 240) {
          lift = Math.max(lift, vh - r.top);
        }
      });
      this.style.setProperty('--sid-nl-lift', lift ? `${Math.round(lift + 12)}px` : '0px');
    }

    open({ focus = true } = {}) {
      if (this.isOpen) return;
      this.isOpen = true;
      this.liftAboveBottomBars();
      this.classList.add('is-open');
      document.documentElement.classList.add('sid-nl-open');
      this.teaser.setAttribute('aria-expanded', 'true');
      if (focus) requestAnimationFrame(() => this.panel.focus({ preventScroll: true }));
    }

    // remember: weggeklikt = klein houden (niet meer vanzelf openen); uit voor de theme-editor
    close({ remember = true } = {}) {
      if (!this.isOpen) return;
      if (remember) this.remember(COLLAPSED_KEY);
      const hadFocus = this.panel.contains(document.activeElement);
      this.isOpen = false;
      this.classList.remove('is-open');
      document.documentElement.classList.remove('sid-nl-open');
      this.teaser.setAttribute('aria-expanded', 'false');
      this.updateTeaser();
      if (hadFocus) {
        if (!this.teaser.hidden) this.teaser.focus({ preventScroll: true });
        else document.activeElement.blur();
      }
    }

    decline() {
      this.remember(DECLINED_KEY);
      this.close();
    }
  });
}
